import { useState } from 'react';
import { Icon, PenIcon } from '../components/ui/Icon';
import { IconButton, Sheet } from '../components/ui/ui';
import type { NoteImage, Tool } from '../storage/schemas';

/** Reference editor toolbar: checklist · Aa · pen · image+ · trash, evenly spaced under the card. */
export function EditorToolbar({
  onChecklist,
  onChecklistLong,
  checklistActive,
  onText,
  onDraw,
  onImage,
  onTrash,
  penColor,
}: {
  onChecklist: () => void;
  onChecklistLong: () => void;
  checklistActive: boolean;
  onText: () => void;
  onDraw: () => void;
  onImage: () => void;
  onTrash: () => void;
  penColor: string;
}) {
  let pressTimer: ReturnType<typeof setTimeout> | null = null;
  let longFired = false;
  return (
    <div className="editor-toolbar" role="toolbar" aria-label="Note tools">
      <button
        type="button"
        className={`icon-btn tb-btn ${checklistActive ? 'is-pressed' : ''}`}
        aria-label="Checklist (press and hold to organise)"
        title="Checklist — press and hold to organise"
        aria-pressed={checklistActive}
        onPointerDown={(e) => {
          e.preventDefault(); // keep focus in the text so the toggle applies to the current line
          longFired = false;
          pressTimer = setTimeout(() => {
            longFired = true;
            onChecklistLong();
          }, 550);
        }}
        onPointerUp={() => pressTimer && clearTimeout(pressTimer)}
        onPointerLeave={() => pressTimer && clearTimeout(pressTimer)}
        onClick={() => {
          if (!longFired) onChecklist();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          onChecklistLong();
        }}
      >
        <Icon name="checklist" />
      </button>
      <IconButton className="tb-btn" icon="text" label="Text and design" onClick={onText} />
      <IconButton className="tb-btn" label="Draw" onClick={onDraw}>
        <PenIcon tip={penColor} />
      </IconButton>
      <IconButton className="tb-btn" icon="image" label="Add image" onClick={onImage} />
      <IconButton className="tb-btn" icon="trash" label="Delete or start a new page" onClick={onTrash} />
    </div>
  );
}

export const PEN_COLORS = ['#171717', '#FFFFFF', '#E53935', '#FF8700', '#FFC400', '#43A047', '#1E88E5', '#3949AB', '#8E24AA', '#EC407A', '#8D6E63', '#9E9E9E', '#00ACC1', '#C0CA33', '#F48FB1', '#6D4C41'];
const TOOLS: { tool: Tool; label: string; icon: 'pen' | 'marker' | 'crayon' | 'highlighter' | 'eraser' }[] = [
  { tool: 'pen', label: 'Pen', icon: 'pen' },
  { tool: 'marker', label: 'Marker', icon: 'marker' },
  { tool: 'crayon', label: 'Crayon', icon: 'crayon' },
  { tool: 'highlighter', label: 'Highlighter', icon: 'highlighter' },
  { tool: 'eraser', label: 'Eraser', icon: 'eraser' },
];

/** Reference drawing bar: ✕ (filled) · undo · redo · brush · colour ring. */
export function DrawingToolbar({
  onClose,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  tool,
  setTool,
  color,
  setColor,
  size,
  setSize,
}: {
  onClose: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  tool: Tool;
  setTool: (t: Tool) => void;
  color: string;
  setColor: (c: string) => void;
  size: number;
  setSize: (s: number) => void;
}) {
  const [panel, setPanel] = useState<'none' | 'brush' | 'color'>('none');
  return (
    <div className="drawing-bar-wrap">
      {panel === 'brush' && (
        <div className="draw-panel" role="group" aria-label="Brush">
          <div className="draw-tools">
            {TOOLS.map((t) => (
              <IconButton key={t.tool} icon={t.icon} label={t.label} pressed={tool === t.tool} variant="circle" onClick={() => setTool(t.tool)} />
            ))}
          </div>
          <label className="draw-size">
            Size
            <input type="range" min={0.003} max={0.03} step={0.001} value={size} onChange={(e) => setSize(Number(e.target.value))} aria-label="Brush size" />
            <span className="draw-size-dot" style={{ width: `${Math.max(4, size * 900)}px`, height: `${Math.max(4, size * 900)}px`, background: tool === 'eraser' ? '#fff' : color }} />
          </label>
        </div>
      )}
      {panel === 'color' && (
        <div className="draw-panel" role="group" aria-label="Colour">
          <div className="draw-colors">
            {PEN_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={`draw-color ${color === c ? 'is-on' : ''}`}
                style={{ background: c }}
                aria-label={`Colour ${c}`}
                aria-pressed={color === c}
                onClick={() => {
                  setColor(c);
                  if (tool === 'eraser') setTool('pen');
                }}
              />
            ))}
            <input type="color" value={color} onChange={(e) => setColor(e.target.value.toUpperCase())} aria-label="Custom colour" />
          </div>
        </div>
      )}
      <div className="editor-toolbar is-drawing" role="toolbar" aria-label="Drawing tools">
        <IconButton icon="close" label="Done drawing" variant="filled" onClick={onClose} />
        <IconButton className="tb-btn" icon="undo" label="Undo" onClick={onUndo} disabled={!canUndo} />
        <IconButton className="tb-btn" icon="redo" label="Redo" onClick={onRedo} disabled={!canRedo} />
        <IconButton className="tb-btn" icon={tool === 'eraser' ? 'eraser' : 'brush'} label="Brush and eraser" pressed={panel === 'brush'} onClick={() => setPanel(panel === 'brush' ? 'none' : 'brush')} />
        <button type="button" className={`icon-btn tb-btn color-ring ${panel === 'color' ? 'is-pressed' : ''}`} aria-label="Colour" aria-pressed={panel === 'color'} onClick={() => setPanel(panel === 'color' ? 'none' : 'color')}>
          <span style={{ background: color }} />
        </button>
      </div>
    </div>
  );
}

export function ImageToolbar({
  image,
  onChange,
  onLayer,
  onDelete,
  onCrop,
  onCutOut,
  onDone,
}: {
  image: NoteImage;
  onChange: (patch: Partial<NoteImage>) => void;
  onLayer: (dir: 1 | -1) => void;
  onDelete: () => void;
  onCrop: () => void;
  onCutOut: () => void;
  onDone: () => void;
}) {
  return (
    <div className="image-toolbar" role="toolbar" aria-label="Image tools">
      <IconButton icon="layer-up" label="Bring forward" onClick={() => onLayer(1)} disabled={image.locked} />
      <IconButton icon="layer-down" label="Send backward" onClick={() => onLayer(-1)} disabled={image.locked} />
      <button type="button" className="chip" aria-pressed={image.aboveText} onClick={() => onChange({ aboveText: !image.aboveText })} disabled={image.locked}>
        {image.aboveText ? 'Over text' : 'Under text'}
      </button>
      <IconButton icon="rotate" label="Rotate 90°" onClick={() => onChange({ rotation: Math.round(((image.rotation + 90) % 360) * 10) / 10 })} disabled={image.locked} />
      <IconButton icon="crop" label="Crop" onClick={onCrop} disabled={image.locked} />
      <IconButton icon="wand" label="Cut out background" onClick={onCutOut} disabled={image.locked} />
      <IconButton icon={image.locked ? 'lock' : 'unlock'} label={image.locked ? 'Unlock image' : 'Lock image'} pressed={image.locked} onClick={() => onChange({ locked: !image.locked })} />
      <IconButton icon="trash" label="Remove image" onClick={onDelete} />
      <IconButton icon="check" label="Done" onClick={onDone} />
    </div>
  );
}

export function CropSheet({ open, onClose, image, onChange }: { open: boolean; onClose: () => void; image: NoteImage | null; onChange: (patch: Partial<NoteImage>) => void }) {
  if (!image) return null;
  const c = image.crop;
  const field = (k: 't' | 'r' | 'b' | 'l', label: string) => (
    <label className="crop-field" key={k}>
      <span>{label}</span>
      <input type="range" min={0} max={0.45} step={0.01} value={c[k]} onChange={(e) => onChange({ crop: { ...c, [k]: Number(e.target.value) } })} aria-label={`Crop ${label}`} />
      <output>{Math.round(c[k] * 100)}%</output>
    </label>
  );
  return (
    <Sheet open={open} onClose={onClose} title="Crop image">
      <div className="crop-pane">
        {field('t', 'Top')}
        {field('b', 'Bottom')}
        {field('l', 'Left')}
        {field('r', 'Right')}
        <button type="button" className="btn" onClick={() => onChange({ crop: { t: 0, r: 0, b: 0, l: 0 } })}>
          Reset crop
        </button>
      </div>
    </Sheet>
  );
}
